import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, CreditCard, ArrowLeft, AlertTriangle, Sparkles } from 'lucide-react';
import { isIOSApp } from '../utils/platform';
import IOSRestrictionMessage from '../components/IOSRestrictionMessage';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const SubscriptionSetupPage = () => {
  const { user, isLoaded } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [error, setError] = useState('');
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month'); // 'month' or 'year'
  
  const backendUrl = API_BASE_URL;

  // Fetch subscription status on mount
  useEffect(() => {
    const fetchStatus = async () => {
      if (!isLoaded || !user) return;

      try {
        setLoading(true);
        const response = await axios.get(
          `${backendUrl}/api/clerk/subscription-status?clerk_user_id=${user.id}`,
          {
            withCredentials: true,
            timeout: 10000
          }
        );

        const data = response.data;
        setSubscriptionStatus(data);

        console.log('[SubscriptionSetup] Status:', data);

        // Only auto-redirect if the user has a CONFIRMED PAID subscription
        // (i.e. they have at least one Stripe subscription on file AND they are NOT on
        // a trial). Trial users and FREE users who land here came to subscribe — let
        // them stay on the page so they can click "Complete Payment & Start Trial"
        // and reach Stripe checkout.
        const hasPaidSubscription =
          (data.subscriptions_count || 0) > 0 &&
          !data.trial_end &&
          data.subscription_status === 'active';

        if (hasPaidSubscription) {
          console.log('[SubscriptionSetup] User already has a paid subscription, redirecting to dashboard');
          setTimeout(() => navigate('/dashboard', { replace: true }), 1000);
        }
      } catch (err) {
        console.error('[SubscriptionSetup] Error fetching status:', err);
        setError('Failed to load subscription status. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [isLoaded, user, backendUrl, navigate]);

  // Apple App Store Guideline 3.1.1 compliance
  // Block subscription setup in iOS app - users must subscribe via website
  if (isIOSApp()) {
    return <IOSRestrictionMessage showSignIn={true} />;
  }

  const handleCompleteSubscription = async () => {
    if (!user || !subscriptionStatus) return;

    try {
      setCreatingCheckout(true);
      setError('');

      const plan = subscriptionStatus.plan.toLowerCase();
      
      // Create Stripe Checkout session with selected billing interval
      const response = await axios.post(
        `${backendUrl}/api/clerk/create-checkout`,
        {
          clerk_user_id: user.id,
          plan: plan,
          interval: billingInterval,
          success_url: `${window.location.origin}/dashboard?checkout=success`,
          cancel_url: `${window.location.origin}/subscription-setup?checkout=cancelled`
        },
        {
          withCredentials: true,
          timeout: 10000
        }
      );

      console.log('[SubscriptionSetup] Checkout session created:', response.data);

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('[SubscriptionSetup] Error creating checkout:', err);
      setError(
        err.response?.data?.detail || 
        'Failed to create checkout session. Please try again.'
      );
      setCreatingCheckout(false);
    }
  };

  const handleSkipForNow = () => {
    // User wants to skip payment - downgrade to FREE and go to dashboard
    console.log('[SubscriptionSetup] User skipped payment, redirecting to dashboard');
    navigate('/dashboard', { replace: true });
  };

  if (!isLoaded || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  const planDetails = {
    STARTER: {
      name: 'Starter',
      monthlyPrice: '$19.99',
      annualPrice: '$199',
      period: 'month',
      features: [
        'All Free tools included',
        'Save up to 10 deals',
        'Share links with clients',
        'Branded PDF reports',
        'Portfolio basics'
      ],
      color: 'from-blue-600 to-blue-700'
    },
    PRO: {
      name: 'Pro',
      monthlyPrice: '$49.99',
      annualPrice: '$549',
      period: 'month',
      features: [
        'All Starter features included',
        'Agent P&L Tracker',
        'Exportable PDF/Excel reports',
        'Unlimited deals & portfolios',
        'URL prefill from listings',
        '5-year projections',
        'Multi-brand profiles',
        'All future Pro features'
      ],
      color: 'from-emerald-600 to-emerald-700'
    }
  };

  const currentPlanDetails = planDetails[subscriptionStatus?.plan] || planDetails.PRO;
  const displayPrice = billingInterval === 'year' ? currentPlanDetails.annualPrice : currentPlanDetails.monthlyPrice;
  const displayPeriod = billingInterval === 'year' ? 'year' : 'month';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4">
      <div className="max-w-2xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/pricing')}
            className="mb-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Pricing
          </Button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Complete Your Subscription
          </h1>
          <p className="text-gray-600">
            You're almost there! Complete your payment to unlock all {currentPlanDetails.name} features.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        )}

        {/* Plan Summary Card */}
        <Card className="mb-6 shadow-lg">
          <CardHeader className={`bg-gradient-to-r ${currentPlanDetails.color} text-white rounded-t-lg`}>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">{currentPlanDetails.name} Plan</CardTitle>
                <CardDescription className="text-white/90 mt-1">
                  Professional tools for your real estate business
                </CardDescription>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">{displayPrice}</div>
                <div className="text-sm text-white/80">per {displayPeriod}</div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
              <span className={`text-sm font-medium ${billingInterval === 'month' ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingInterval(billingInterval === 'month' ? 'year' : 'month')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  billingInterval === 'year' ? 'bg-emerald-600' : 'bg-gray-300'
                }`}
                data-testid="billing-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    billingInterval === 'year' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${billingInterval === 'year' ? 'text-gray-900' : 'text-gray-500'}`}>
                Annual
              </span>
              {billingInterval === 'year' && (
                <span className="bg-emerald-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  1 month free
                </span>
              )}
            </div>
            
            {billingInterval === 'year' && (
              <div className="text-center text-sm text-emerald-700 mb-4">
                Save $50.89 compared to monthly billing
              </div>
            )}
            
            <div className="mb-4 flex items-center text-sm text-blue-600 bg-blue-50 p-3 rounded-lg">
              <Sparkles className="w-4 h-4 mr-2" />
              <span className="font-semibold">7-day free trial - No credit card required</span>
            </div>
            
            <h3 className="font-semibold text-gray-900 mb-3">What's included:</h3>
            <ul className="space-y-2">
              {currentPlanDetails.features.map((feature, idx) => (
                <li key={idx} className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-4">
          <Button
            onClick={handleCompleteSubscription}
            disabled={creatingCheckout}
            className={`w-full h-12 text-lg bg-gradient-to-r ${currentPlanDetails.color} hover:from-emerald-700 hover:to-emerald-800 text-white`}
          >
            {creatingCheckout ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Creating Checkout Session...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Complete Payment & Start Trial
              </>
            )}
          </Button>

          <Button
            variant="ghost"
            onClick={handleSkipForNow}
            disabled={creatingCheckout}
            className="w-full text-gray-600 hover:text-gray-900"
          >
            Skip for now (Use Free Plan)
          </Button>
        </div>

        {/* Payment Info */}
        <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2 text-sm">Payment Information</h4>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• Secure payment processing by Stripe</li>
            <li>• 7-day free trial included</li>
            <li>• Cancel anytime from your dashboard</li>
            <li>• Automatic billing after trial ends</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSetupPage;
